import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import { supabase } from '../utils/supabaseClient';
import { useModal } from '../context/ModalContext';
import MemberCenterLayout from '../components/common/MemberCenterLayout';
import MemberFormSaveButton from '../components/common/MemberFormSaveButton';
import './TravelProfile.css';

import {
  STAY_OPTIONS, TRAVEL_STYLE_OPTIONS, ACTIVITY_TIME_OPTIONS, FOOD_OPTIONS,
  ADVENTURE_OPTIONS, LANGUAGE_OPTIONS, SKILL_OPTIONS, RED_FLAG_OPTIONS, INTEREST_OPTIONS
} from '../utils/travelProfileConstants';

const MBTI_OPTIONS = ['INTJ', 'INTP', 'ENTJ', 'ENTP', 'INFJ', 'INFP', 'ENFJ', 'ENFP', 'ISTJ', 'ISFJ', 'ESTJ', 'ESFJ', 'ISTP', 'ISFP', 'ESTP', 'ESFP'];

const ZODIAC_OPTIONS = ['牡羊座', '金牛座', '雙子座', '巨蟹座', '獅子座', '處女座', '天秤座', '天蠍座', '射手座', '摩羯座', '水瓶座', '雙魚座'];

const GENDER_OPTIONS = [
  { value: 'female', label: '女性 Female' },
  { value: 'male', label: '男性 Male' },
  { value: 'non_binary', label: '非二元性別 Non-Binary' },
  { value: 'prefer_not_to_say', label: '不願透露 Prefer Not to Say' },
];

function SectionTitle({ children }) {
  return (
    <h3 className="travel-profile-section-title">
      {children}
    </h3>
  );
}

function ChipGroup({ options, selected, onChange, multi = true }) {
  const toggle = (value) => {
    if (multi) {
      onChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
    } else {
      onChange(selected === value ? null : value);
    }
  };
  const isSelected = (value) => multi ? selected.includes(value) : selected === value;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
      {options.map(opt => (
        <button
          type="button"
          key={opt.value}
          onClick={() => toggle(opt.value)}
          style={{
            padding: '6px 14px', borderRadius: '20px', fontSize: '13px', cursor: 'pointer',
            border: isSelected(opt.value) ? '1.5px solid #4e8bca' : '1.5px solid #e5e7eb',
            background: isSelected(opt.value) ? '#eef4f9' : 'white',
            color: isSelected(opt.value) ? '#4e8bca' : '#6b7280',
            fontWeight: isSelected(opt.value) ? '600' : '400',
            transition: 'all 0.15s',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function TravelProfile() {
  const navigate = useNavigate();
  const { currentUser } = useContext(UserContext);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [newTrip, setNewTrip] = useState('');
  const { showAlert } = useModal();

  const [form, setForm] = useState({
    mbti: '',
    cigarette: false,
    nationality: '',
    gender: '',
    zodiac: '',
    stay_pref: [],
    travel_style: '',
    activity_time: [],
    food_option: [],
    adventure_level: [],
    interests: [],
    languages: [],
    skills: [],
    introduction: '',
    past_trips: [],
    red_flags: [],
  });

  const loadProfile = useCallback(async () => {
    if (!currentUser) return;
    try {
      const { data, error } = await supabase
        .from('travel_profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        setForm({
          mbti: data.mbti || '',
          cigarette: data.cigarette || false,
          nationality: data.nationality || '',
          gender: data.gender || '',
          zodiac: data.zodiac || '',
          stay_pref: data.stay_pref || [],
          travel_style: data.travel_style || '',
          activity_time: data.activity_time || [],
          food_option: data.food_option || [],
          adventure_level: data.adventure_level || [],
          interests: data.interests || [],
          languages: data.languages || [],
          skills: data.skills || [],
          introduction: data.introduction || '',
          past_trips: data.past_trips || [],
          red_flags: data.red_flags || [],
        });
      }
    } catch (err) {
      console.error('loadProfile error:', err);
      showAlert('錯誤', '載入旅伴檔案失敗');
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, showAlert]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSave = async () => {
    if (!currentUser) return;
    setIsSaving(true);
    try {
      const payload = {
        user_id: currentUser.id,
        mbti: form.mbti || null,
        cigarette: form.cigarette,
        nationality: form.nationality || null,
        gender: form.gender || null,
        zodiac: form.zodiac || null,
        stay_pref: form.stay_pref,
        travel_style: form.travel_style || null,
        activity_time: form.activity_time,
        food_option: form.food_option,
        adventure_level: form.adventure_level,
        interests: form.interests,
        languages: form.languages,
        skills: form.skills,
        introduction: form.introduction || null,
        past_trips: form.past_trips,
        red_flags: form.red_flags,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('travel_profiles')
        .upsert(payload, { onConflict: 'user_id' });
      if (error) throw error;

      showAlert('成功', '旅伴檔案已儲存！');
      navigate('/travel');
    } catch (err) {
      console.error('handleSave error:', err);
      showAlert('錯誤', '儲存失敗，請稍後再試');
    } finally {
      setIsSaving(false);
    }
  };

  const addTrip = () => {
    const trip = newTrip.trim();
    if (!trip) return;
    setForm(f => ({ ...f, past_trips: [...f.past_trips, trip] }));
    setNewTrip('');
  };

  const removeTrip = (index) => {
    setForm(f => ({ ...f, past_trips: f.past_trips.filter((_, i) => i !== index) }));
  };

  if (isLoading) {
    return (
      <MemberCenterLayout title="我的旅伴檔案" subtitle="完善檔案，找到最契合的旅行夥伴" activeKey="travel-profile">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
          <div style={{ width: '36px', height: '36px', border: '3px solid #eef4f9', borderTop: '3px solid #4e8bca', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </MemberCenterLayout>
    );
  }

  const inputStyle = {
    width: '100%', minHeight: '46px', padding: '11px 13px', borderRadius: '12px', border: '1px solid #dbe4ee',
    background: '#f8fafc', fontSize: '14px', outline: 'none', boxSizing: 'border-box', color: '#111827',
  };

  const selectStyle = { ...inputStyle };

  return (
    <MemberCenterLayout title="我的旅伴檔案" subtitle="完善檔案，找到最契合的旅行夥伴" activeKey="travel-profile">
      <div className="travel-profile-form">
          {/* About Me */}
          <div className="travel-profile-card">
            <SectionTitle>關於我 About Me</SectionTitle>

            <div className="travel-profile-grid">
              <div>
                <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>MBTI</label>
                <select value={form.mbti} onChange={e => setForm(f => ({ ...f, mbti: e.target.value }))} style={selectStyle}>
                  <option value="">選擇</option>
                  {MBTI_OPTIONS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>星座 Zodiac</label>
                <select value={form.zodiac} onChange={e => setForm(f => ({ ...f, zodiac: e.target.value }))} style={selectStyle}>
                  <option value="">選擇</option>
                  {ZODIAC_OPTIONS.map(z => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>國籍 Nationality</label>
              <input value={form.nationality} onChange={e => setForm(f => ({ ...f, nationality: e.target.value }))} placeholder="例：台灣 Taiwan" style={inputStyle} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>性別 Sex</label>
              <ChipGroup options={GENDER_OPTIONS} selected={form.gender} onChange={v => setForm(f => ({ ...f, gender: v }))} multi={false} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>吸菸 Cigarette</label>
              <ChipGroup
                options={[{ value: true, label: '是 Yes' }, { value: false, label: '否 No' }]}
                selected={form.cigarette}
                onChange={v => setForm(f => ({ ...f, cigarette: v }))}
                multi={false}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>住宿偏好 Stay</label>
              <ChipGroup options={STAY_OPTIONS} selected={form.stay_pref} onChange={v => setForm(f => ({ ...f, stay_pref: v }))} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>旅行風格 Travel Style</label>
              <ChipGroup options={TRAVEL_STYLE_OPTIONS} selected={form.travel_style} onChange={v => setForm(f => ({ ...f, travel_style: v }))} multi={false} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>活動時段 Activity Time</label>
              <ChipGroup options={ACTIVITY_TIME_OPTIONS} selected={form.activity_time} onChange={v => setForm(f => ({ ...f, activity_time: v }))} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>飲食偏好 Food Option</label>
              <ChipGroup options={FOOD_OPTIONS} selected={form.food_option} onChange={v => setForm(f => ({ ...f, food_option: v }))} />
            </div>

            <div style={{ marginBottom: '8px' }}>
              <label style={{ fontSize: '13px', color: '#6b7280', display: 'block', marginBottom: '6px' }}>冒險類型 Adventure Level</label>
              <ChipGroup options={ADVENTURE_OPTIONS} selected={form.adventure_level} onChange={v => setForm(f => ({ ...f, adventure_level: v }))} />
            </div>
          </div>

          {/* Interests & Languages & Skills */}
          <div className="travel-profile-card">
            {/* Interests */}
            <SectionTitle>興趣 My Interests</SectionTitle>
            <ChipGroup options={INTEREST_OPTIONS} selected={form.interests} onChange={v => setForm(f => ({ ...f, interests: v }))} />

            {/* Languages */}
            <div className="travel-profile-subsection">
              <SectionTitle>語言 Languages</SectionTitle>
              <ChipGroup options={LANGUAGE_OPTIONS} selected={form.languages} onChange={v => setForm(f => ({ ...f, languages: v }))} />
            </div>

            {/* Skills */}
            <div className="travel-profile-subsection">
              <SectionTitle>技能 Skills</SectionTitle>
              <ChipGroup options={SKILL_OPTIONS} selected={form.skills} onChange={v => setForm(f => ({ ...f, skills: v }))} />
            </div>
          </div>

          {/* Introduction & Trips & Red Flags */}
          <div className="travel-profile-card">
            {/* Introduction */}
            <SectionTitle>自我介紹 Introduction</SectionTitle>
            <textarea
              value={form.introduction}
              onChange={e => setForm(f => ({ ...f, introduction: e.target.value }))}
              placeholder="介紹一下你的旅行個性、喜歡的旅遊方式..."
              rows={4}
              style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6' }}
            />

            {/* Past Trips */}
            <div className="travel-profile-subsection">
              <SectionTitle>旅行足跡 Travel Footprints</SectionTitle>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <input
                  value={newTrip}
                  onChange={e => setNewTrip(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTrip()}
                  placeholder="例：日本大阪 2024"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button type="button" onClick={addTrip} style={{ background: '#4e8bca', color: 'white', border: 'none', borderRadius: '10px', padding: '10px 16px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', whiteSpace: 'nowrap' }}>
                  新增
                </button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {form.past_trips.map((trip, i) => (
                  <span key={i} style={{ background: '#f3f4f6', borderRadius: '20px', padding: '6px 12px', fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {trip}
                    <button type="button" aria-label={`移除旅行足跡 ${trip}`} onClick={() => removeTrip(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: '0', fontSize: '14px', lineHeight: 1 }}>×</button>
                  </span>
                ))}
              </div>
            </div>

            {/* Red Flags */}
            <div className="travel-profile-subsection">
              <SectionTitle>紅旗 Red Flags（我無法接受的行為）</SectionTitle>
              <ChipGroup options={RED_FLAG_OPTIONS} selected={form.red_flags} onChange={v => setForm(f => ({ ...f, red_flags: v }))} />
            </div>
          </div>

          {/* 底部儲存按鈕 */}
          <MemberFormSaveButton onClick={handleSave} isSaving={isSaving} label="儲存旅伴檔案" />
      </div>
    </MemberCenterLayout>
  );
}
